import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { StepV2ConfirmButton } from '../../common/v2/StepV2ConfirmButton';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  InfoV2Screen,
  InfoV2ScreenButtonWrapper,
  InfoV2ScreenContentWrapper,
  InfoV2ScreenDescription,
  InfoV2ScreenDesktop,
  InfoV2ScreenDesktopLeft,
  InfoV2ScreenDesktopRight,
  InfoV2ScreenImgDesktop,
  InfoV2ScreenImgMobile,
  InfoV2ScreenMobile,
  InfoV2ScreenMobileButtonWrapper,
  InfoV2ScreenMobileImgWrapper,
  InfoV2ScreenSection,
  InfoV2ScreenTitle,
} from './InfoV2Step.style';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { InfoV2GuidesCarousel } from './InfoV2GuidesCarousel';

interface SegmentProps {
  title: string;
  titleMobile?: string;
  description: string;
  backgroundImageUrl: string;
  mobileBackgroundImageUrl?: string;
}

interface InfoV2StepProps extends BaseStepPropsV2 {
  propsBySegment: Record<string, SegmentProps>;
  section: string;
  imageFullSize?: boolean;
  withGuidesCarousel?: boolean;
}

export const InfoV2Step: React.FC<InfoV2StepProps> = observer((props) => {
  const {
    nextStep,
    ctaButtonTitle,
    isActive,
    section,
    propsBySegment,
    imageFullSize,
    stepName,
    withGuidesCarousel,
    goToNamedStep,
    nextStepId,
  } = props;

  const { UserDiscoveryResponse } = useMst();
  const segment = UserDiscoveryResponse?.segment as string;
  const title = propsBySegment[segment]?.title;
  const titleMobile = propsBySegment[segment]?.titleMobile;
  const description = propsBySegment[segment]?.description;
  const backgroundImageUrl = propsBySegment[segment]?.backgroundImageUrl;
  const mobileBackgroundImageUrl = propsBySegment[segment]?.mobileBackgroundImageUrl
    ? propsBySegment[segment]?.mobileBackgroundImageUrl
    : propsBySegment[segment]?.backgroundImageUrl;

  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      analytics.track(`view infoV2`, { stepName, segment: UserDiscoveryResponse.segment });
      return window.scrollTo(0, 0);
    }
  }, [analytics, isActive, stepName, UserDiscoveryResponse.segment]);

  const handleOnClick = () => {
    analytics.track(`click infoV2`, { stepName, segment: UserDiscoveryResponse.segment });
    if (!nextStepId) return nextStep();
    goToNamedStep(nextStepId);
  };

  const onBack = () => {
    props.previousStep();
  };

  return (
    <BaseStepV3 {...props}>
      <InfoV2Screen>
        <AbsoluteHeader onBack={onBack} color="black" />
        <InfoV2ScreenDesktop>
          <InfoV2ScreenDesktopLeft>
            <InfoV2ScreenContentWrapper>
              <InfoV2ScreenSection>{section}</InfoV2ScreenSection>
              <InfoV2ScreenTitle dangerouslySetInnerHTML={{ __html: title }} />
              <InfoV2ScreenDescription>{description}</InfoV2ScreenDescription>
              <InfoV2ScreenButtonWrapper>
                <StepV2ConfirmButton
                  {...props}
                  isButtonDisabled={false}
                  isLoading={false}
                  onClick={handleOnClick}
                  ctaButtonTitle={ctaButtonTitle}
                  variant={MainButtonVariant.Black}
                ></StepV2ConfirmButton>
              </InfoV2ScreenButtonWrapper>
            </InfoV2ScreenContentWrapper>
          </InfoV2ScreenDesktopLeft>
          <InfoV2ScreenDesktopRight imageFullSize={imageFullSize}>
            {withGuidesCarousel ? (
              <InfoV2GuidesCarousel />
            ) : (
              <InfoV2ScreenImgDesktop img={backgroundImageUrl} imageFullSize={imageFullSize} />
            )}
          </InfoV2ScreenDesktopRight>
        </InfoV2ScreenDesktop>

        <InfoV2ScreenMobile>
          <InfoV2ScreenContentWrapper withGuidesCarousel={withGuidesCarousel}>
            <InfoV2ScreenSection>{section}</InfoV2ScreenSection>
            <InfoV2ScreenTitle dangerouslySetInnerHTML={{ __html: titleMobile ?? title }} />
            <InfoV2ScreenDescription withGuidesCarousel={withGuidesCarousel}>{description}</InfoV2ScreenDescription>
            <InfoV2ScreenMobileImgWrapper>
              {withGuidesCarousel ? <InfoV2GuidesCarousel /> : <InfoV2ScreenImgMobile src={mobileBackgroundImageUrl} />}
            </InfoV2ScreenMobileImgWrapper>
          </InfoV2ScreenContentWrapper>
          <InfoV2ScreenMobileButtonWrapper>
            <StepV2ConfirmButton
              {...props}
              isButtonDisabled={false}
              isLoading={false}
              onClick={handleOnClick}
              ctaButtonTitle={ctaButtonTitle}
              variant={MainButtonVariant.Black}
            ></StepV2ConfirmButton>
          </InfoV2ScreenMobileButtonWrapper>
        </InfoV2ScreenMobile>
      </InfoV2Screen>
    </BaseStepV3>
  );
});
